import * as React from 'react'
import { observer } from 'mobx-react'
import { QuestionEditorComponentProps } from 'jobs/questions/props'
import SelectQuestionViewModel, {
  areSelectOptionsEqual,
  SelectOption,
} from '../../SelectQuestionViewModel'
import {
  useCustomSelectStateFromItemSource,
  CustomSelect,
  ItemSource,
} from 'components/inputs/CustomSelect'
import { ToggleGroup, ToggleItem } from 'components/ToggleGroup'
import { isNotNil } from 'utils/object-util'

interface Props extends QuestionEditorComponentProps<SelectQuestionViewModel> {}

export default observer(function SelectEditorInput(props: Props) {
  return props.vm.options.length >= 20 ? (
    <AdvancedSelect {...props} />
  ) : (
    <SimpleSelect {...props} />
  )
})

const SimpleSelect = observer(function SimpleSelect({
  vm,
  disabled,
  onEnterDown,
  focus$,
  onFocus,
  isNotValid,
}: Props) {
  const keyDown = React.useCallback(
    (e: React.KeyboardEvent) => e.key === 'Enter' && onEnterDown?.(e),
    [onEnterDown],
  )
  return (
    <ToggleGroup stretch multi={vm.multi} focus$={focus$} onFocus={onFocus}>
      {vm.options.map((option) => (
        <ToggleItem
          toggled={vm.isSelected(option)}
          disabled={disabled}
          title={option.text}
          onChange={() => vm.toggleOption(option)}
          key={option.id}
          onKeyDown={keyDown}
          isNotValid={isNotValid}
          dataTest={'select-answer-' + vm.id + '-' + option.id}
        >
          {option.text}
        </ToggleItem>
      ))}
    </ToggleGroup>
  )
})

export const AdvancedSelect = observer(function AdvancedSelect({
  vm,
  disabled,
  onEnterDown,
  focus$,
  onFocus,
  isNotValid,
}: Props) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static<SelectOption>(vm.options, (item, text) =>
        item.text.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.options],
  )
  const change = React.useCallback(
    (values: SelectOption | SelectOption[] | undefined) =>
      vm.change(Array.isArray(values) ? values : [values].filter(isNotNil)),
    [vm],
  )
  const itemToString = React.useCallback((item: SelectOption) => item.text, [])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })
  return (
    <CustomSelect
      {...selectProps}
      focus$={focus$}
      onFocus={onFocus}
      disabled={disabled}
      persist={vm.multi}
      onEnterDown={onEnterDown}
      itemToString={itemToString}
      areItemsEqual={areSelectOptionsEqual}
      multi={vm.multi}
      value={vm.multi ? vm.selected : vm.selected[0]}
      onChange={change}
      placeholder={vm.multi ? 'Select all that apply' : 'Select one'}
      isNotValid={isNotValid}
      data-test={`select-advanced-${vm.id}`}
    />
  )
})
